import Cookies from 'js-cookie'
import { v4 as uuidv4 } from 'uuid'
import { create } from 'zustand'

type UtmParams = {
  source?: string
  medium?: string
  campaign?: string
  term?: string
  content?: string
  ad?: string
}

type eventState = {
  anonymousId: string | undefined
  sessionId: string | undefined
  flowId: string | undefined
  gclid: string | undefined
  fbclid: string | undefined
  irclickid: string | undefined
  utmParams: UtmParams
  setAnonymousId: () => string
  setSessionId: () => string
  setFlowId: () => string
  setGclid: (gclid: string) => void
  setFbclid: (fbclid: string) => void
  setIrclickid: (irclickid: string) => void
  setUtmParams: (params: UtmParams) => void
  clearFlowId: () => void
  clearGclid: () => void
  clearUtmParams: () => void
  clearFbclid: () => void
}

// Helper function to get item from localStorage or cookies
const getStoredValue = (key: string): string | undefined => {
  // Try localStorage first
  const localValue = localStorage.getItem(key)
  if (localValue) return localValue

  // Fall back to cookies for backward compatibility
  return Cookies.get(key)
}

// Helper function to save to both localStorage and cookies
const saveValue = (key: string, value: string, expiryDays = 30): void => {
  localStorage.setItem(key, value)
  Cookies.set(key, value, { expires: expiryDays })
}

// Helper function to remove value from both localStorage and cookies
const removeValue = (key: string): void => {
  localStorage.removeItem(key)
  Cookies.remove(key)
}

export const useEventStore = create<eventState>((set) => ({
  anonymousId: Cookies.get('anonymousId') || undefined,
  sessionId: sessionStorage.getItem('sessionId') || undefined,
  flowId: undefined,
  gclid: getStoredValue('gclid') || undefined,
  fbclid: getStoredValue('fbclid') || undefined,
  irclickid: getStoredValue('irclickid') || undefined,
  utmParams: {
    source: getStoredValue('utm_source') || undefined,
    medium: getStoredValue('utm_medium') || undefined,
    campaign: getStoredValue('utm_campaign') || undefined,
    term: getStoredValue('utm_term') || undefined,
    content: getStoredValue('utm_content') || undefined,
    ad: getStoredValue('utm_ad') || undefined,
  },
  setAnonymousId: () => {
    const newId = uuidv4()
    Cookies.set('anonymousId', newId, { expires: 365 })
    set({ anonymousId: newId })
    return newId
  },
  setSessionId: () => {
    const newId = uuidv4()
    sessionStorage.setItem('sessionId', newId)
    set({ sessionId: newId })
    return newId
  },
  setFlowId: () => {
    const newId = uuidv4()
    set({ flowId: newId })
    return newId
  },
  setGclid: (gclid: string) => {
    saveValue('gclid', gclid, 30) // Store for 30 days
    set({ gclid })
  },
  setFbclid: (fbclid: string) => {
    saveValue('fbclid', fbclid, 30) // Store for 30 days
    set({ fbclid })
  },
  setIrclickid: (irclickid: string) => {
    saveValue('irclickid', irclickid, 30) // Store for 30 days
    set({ irclickid })
  },
  setUtmParams: (params: UtmParams) => {
    // Get current stored parameters
    const currentParams = {
      source: getStoredValue('utm_source') || undefined,
      medium: getStoredValue('utm_medium') || undefined,
      campaign: getStoredValue('utm_campaign') || undefined,
      term: getStoredValue('utm_term') || undefined,
      content: getStoredValue('utm_content') || undefined,
      ad: getStoredValue('utm_ad') || undefined,
    }

    // Create merged parameters by keeping current values unless new ones are provided
    const mergedParams = {
      ...currentParams,
      ...params,
    }

    // Store each UTM parameter if it exists
    if (params.source !== undefined) {
      if (params.source) {
        saveValue('utm_source', params.source, 30)
      } else {
        removeValue('utm_source')
      }
    }

    if (params.medium !== undefined) {
      if (params.medium) {
        saveValue('utm_medium', params.medium, 30)
      } else {
        removeValue('utm_medium')
      }
    }

    if (params.campaign !== undefined) {
      if (params.campaign) {
        saveValue('utm_campaign', params.campaign, 30)
      } else {
        removeValue('utm_campaign')
      }
    }

    if (params.term !== undefined) {
      if (params.term) {
        saveValue('utm_term', params.term, 30)
      } else {
        removeValue('utm_term')
      }
    }

    if (params.content !== undefined) {
      if (params.content) {
        saveValue('utm_content', params.content, 30)
      } else {
        removeValue('utm_content')
      }
    }

    if (params.ad !== undefined) {
      if (params.ad) {
        saveValue('utm_ad', params.ad, 30)
      } else {
        removeValue('utm_ad')
      }
    }

    // Update the store with merged parameters
    set({ utmParams: mergedParams })
  },
  clearFlowId: () => {
    set({ flowId: undefined })
  },
  clearGclid: () => {
    removeValue('gclid')
    set({ gclid: undefined })
  },
  clearFbclid: () => {
    removeValue('fbclid')
    set({ fbclid: undefined })
  },
  clearUtmParams: () => {
    removeValue('utm_source')
    removeValue('utm_medium')
    removeValue('utm_campaign')
    removeValue('utm_term')
    removeValue('utm_content')
    removeValue('utm_ad')
    set({
      utmParams: {
        source: undefined,
        medium: undefined,
        campaign: undefined,
        term: undefined,
        content: undefined,
        ad: undefined,
      },
    })
  },
}))
